import { useEffect, useCallback } from 'react';
import { ListImperativeAPI } from 'react-window';

/**
 * useKeyboardScrolling - Enhanced keyboard navigation for document scrolling
 * 
 * Provides Page Up/Down support that scrolls by exactly one page (like MS Word),
 * and Home/End to jump to beginning/end of document.
 * 
 * Features:
 * - Page Up/Down scrolls by one page height
 * - Ctrl+Home jumps to document start
 * - Ctrl+End jumps to document end
 * - Space bar scrolls down (when not in text input)
 * - Respects existing text editing keyboard handlers
 */
interface UseKeyboardScrollingOptions {
  /** Ref to react-window List for scroll control */
  listRef: React.RefObject<ListImperativeAPI | null>;
  /** Height of each page in pixels */
  pageHeight: number;
  /** Gap between pages */
  pageGap: number;
  /** Scale factor */
  scale: number;
  /** Total number of pages */
  pageCount: number;
  /** Whether scrolling is enabled (disable during text input) */
  enabled?: boolean;
  /** Current scroll position getter */
  getCurrentScroll?: () => number;
}

export const useKeyboardScrolling = ({
  listRef,
  pageHeight,
  pageGap,
  scale,
  pageCount,
  enabled = true,
  getCurrentScroll
}: UseKeyboardScrollingOptions) => {
  
  // Calculate page height including gap
  const pageWithGap = pageHeight * scale + pageGap;

  // Scroll to a specific page (0-indexed internally)
  const scrollToPage = useCallback((pageIndex: number, behavior: 'auto' | 'smooth' = 'smooth') => {
    if (!listRef.current) return;
    
    // Clamp to valid range
    const clampedIndex = Math.max(0, Math.min(pageIndex, pageCount - 1));
    
    listRef.current.scrollToRow({
      index: clampedIndex,
      align: 'start',
      behavior
    });
  }, [listRef, pageCount]);

  // Scroll by a number of pages
  const scrollByPages = useCallback((deltaPages: number, behavior: 'auto' | 'smooth' = 'smooth') => {
    if (!listRef.current) return;
    
    // Get current scroll position and calculate current page
    const currentScroll = getCurrentScroll?.() ?? 0;
    const currentPage = Math.floor(currentScroll / pageWithGap);
    
    // Calculate target page
    const targetPage = currentPage + deltaPages;
    scrollToPage(targetPage, behavior);
  }, [listRef, getCurrentScroll, pageWithGap, scrollToPage]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!enabled) return;

    // Don't intercept if user is typing in an input/textarea
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    // Also don't intercept if a canvas is focused (text editing mode)
    if (target.tagName === 'CANVAS') {
      return;
    }

    switch (e.key) {
      case 'PageDown':
        e.preventDefault();
        scrollByPages(1);
        break;

      case 'PageUp':
        e.preventDefault();
        scrollByPages(-1);
        break;

      case 'Home':
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          scrollToPage(0);
        }
        break;

      case 'End':
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          scrollToPage(pageCount - 1);
        }
        break;

      case ' ': // Spacebar
        // Only scroll if not in text editing context
        if (!e.shiftKey && target.tagName !== 'CANVAS') {
          e.preventDefault();
          scrollByPages(1);
        }
        break;

      default:
        break;
    }
  }, [enabled, pageCount, scrollByPages, scrollToPage]);

  // Attach keyboard listener
  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, handleKeyDown]);

  return {
    scrollToPage: (page: number) => scrollToPage(page - 1), // Convert to 0-indexed
    scrollByPages,
    scrollToTop: () => scrollToPage(0),
    scrollToBottom: () => scrollToPage(pageCount - 1)
  };
};

export default useKeyboardScrolling;
